import { Component, inject, input, OnInit, output, signal, TemplateRef, viewChild } from "@angular/core";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";

import { Constants } from "../../../constants/constants";
import { FileInputType, Language, PageMode } from "../../../constants/enums";
import { AttachFile, ValidatorModelType } from "../../../models";
import { HelpdeskAttachment } from "../../../models/helpdesk-model";
import { AppService, ValidatorApiService } from "../../../services";
import {
  FormActionButtonsComponent,
  FormInputComponent,
  FormInputDateComponent,
  FormInputFileComponent,
} from "../../form";

@Component({
  selector: "adaa-support-center-attachment",
  imports: [
    FormInputComponent,
    FormInputFileComponent,
    FormInputDateComponent,
    FormActionButtonsComponent,
    TranslatePipe,
    ReactiveFormsModule,
  ],
  templateUrl: "./support-center-attachment.html",
  styleUrl: "./support-center-attachment.scss",
})
export class SupportCenterAttachmentComponent implements OnInit {
  private _modalService = inject(NgbModal);
  private _appService = inject(AppService);
  private _formBuilder = inject(FormBuilder);
  private _translateService = inject(TranslateService);
  private _toastrService = inject(ToastrService);
  private _validatorApiService = inject(ValidatorApiService);

  modal = viewChild.required<TemplateRef<unknown>>("content");
  submitted = signal<boolean>(false);
  attachmentForm: FormGroup = new FormGroup({});
  pageMode = input.required<string>();
  attachment = output<HelpdeskAttachment>();
  PageMode = PageMode;
  allowedFiles: FileInputType[] = [
    FileInputType.doc,
    FileInputType.docx,
    FileInputType.pdf,
    FileInputType.txt,
    FileInputType.xls,
    FileInputType.xlsx,
  ];
  formValidation = signal<ValidatorModelType | undefined>(undefined);

  public get message() {
    return this._translateService.instant("attachment.max_file_size");
  }

  public getFile(): AttachFile {
    const attach = this.attachmentForm.get("file")?.value;
    return {
      id: attach?.id ?? 0,
      filename: attach?.filename,
    };
  }

  public onFilechange(event: AttachFile | null) {
    if (event) {
      this.attachmentForm.get("file")?.setValue({
        id: event.id,
        filename: event.filename,
      });
    } else this.attachmentForm.get("file")?.setValue(null);
  }

  public ngOnInit(): void {
    this._prepareForm();
  }

  public open() {
    const modalRef: NgbModalRef = this._modalService.open(this.modal(), {
      animation: true,
      scrollable: false,
      keyboard: false,
      size: "xl",
      centered: true,
      modalDialogClass: this._appService.language() === Language.Arabic ? "modal-rtl" : "modal-ltr",
    });

    modalRef.dismissed.subscribe(() => this._resetForm());
    modalRef.closed.subscribe(() => this._resetForm());
  }

  private _prepareForm() {
    const isViewMode = this.pageMode() === PageMode.view;

    this.attachmentForm = this._formBuilder.group({
      nameAE: { value: null, disabled: isViewMode },
      nameEN: { value: null, disabled: isViewMode },
      attachDate: { value: Date.now(), disabled: isViewMode },
      file: { value: null, disabled: isViewMode },
    });

    this._validatorApiService
      .applyValidationToForm(Constants.VALIDATORS_CONF_KEY.VALIDATION_ATTACHMENTS, this.attachmentForm)
      .subscribe({
        next: (response) => {
          this.formValidation.set(response.validation);
          this.attachmentForm = response.form;
        },
      });
  }

  private _resetForm() {
    this.attachmentForm.reset();
    this.attachmentForm.markAsPristine();
    this.attachmentForm.markAsUntouched();
    this.submitted.set(false);
  }

  public checkInvalid(control: string) {
    return (
      this.attachmentForm.controls[control]?.errors?.["required"] &&
      (this.attachmentForm.get(control)?.dirty || this.submitted())
    );
  }

  public submit() {
    this.submitted.set(true);
    if (this.attachmentForm.invalid) {
      this._toastrService.error(this._translateService.instant("common.form.label.missing_information"));
      return;
    }
    const result = {
      ...this.attachmentForm.value,
      id: this.attachmentForm.get("file")?.value?.id,
      filename: this.attachmentForm.get("file")?.value?.filename,
      status: Constants.OBJECT_STATUS.ACTIVE,
    };

    delete result.file;
    this.attachment.emit(result);
    this._modalService.dismissAll();
  }
}
